// @flow
/** @jsx jsx */

import GridLayout from '@zapier/common-components/src/components/GridLayout';
import { StaticImage } from '@zapier/common-components/src/components/StaticAsset';
import { Border, Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

import { type Node } from 'react';

const rootCss = ({ border, borderRadius, padding }) =>
  css({
    alignItems: 'center',
    backgroundColor: Colors.storm,
    borderRadius: borderRadius ? Border.radius[borderRadius] : null,
    border,
    display: 'flex',
    padding,
  });

const wrapperCss = css({
  flex: 1,
});

const titleCss = css({
  fontWeight: 700,
  fontSize: '15px',
  margin: 0,
});

const descriptionCss = css({
  margin: '8px 0 0 0',
  fontSize: '13px',
});

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type Alignment =
  | 'auto'
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | 'baseline'
  | 'stretch';

type Props = {
  /** Vertical alignment for the body */
  bodyAlignment?: Alignment,
  /** Text alignment for the help box */
  bodyTextAlignment?: Alignment,
  /** Border style for the help box */
  border?: string,
  /** Border radius for the help box */
  borderRadius?: 'default' | 'large',
  /** The description of the help box. */
  description: Node,
  /** The title of the help box */
  heading: string,
  /** Which tag you want to use? Could be anything from h1-h6 */
  headingTag?: HeadingTag,
  /** Vertical alignment for the help image */
  imageAlignment?: Alignment,
  /** Alt text for the help image */
  imageAlt?: string,
  /** Placement of the help image */
  imageRight?: boolean,
  /** Src for the help image */
  imageSrc?: string,
  /** Padding for the help box */
  padding?: string,
};

const HelpBox = ({
  bodyAlignment,
  bodyTextAlignment,
  border,
  borderRadius,
  description,
  heading,
  headingTag,
  imageAlignment,
  imageAlt,
  imageRight,
  imageSrc,
  padding,
}: Props) => {
  const Heading = headingTag;

  const columns = imageSrc ? (imageRight ? '1fr 80px' : '80px 1fr') : '1fr';
  const bodySection = (
    <section css={{ alignSelf: bodyAlignment, textAlign: bodyTextAlignment }}>
      <Heading css={titleCss}>{heading}</Heading>
      <p css={descriptionCss}>{description}</p>
    </section>
  );
  const imageSection = imageSrc && (
    <section css={{ alignSelf: imageAlignment }}>
      <StaticImage alt={imageAlt} src={imageSrc} />
    </section>
  );

  return (
    <article css={rootCss({ border, borderRadius, padding })}>
      <div css={wrapperCss}>
        <GridLayout columns={columns} gap={20}>
          {!imageRight && imageSection}
          {bodySection}
          {imageRight && imageSection}
        </GridLayout>
      </div>
    </article>
  );
};

HelpBox.defaultProps = {
  bodyAlignment: 'flex-start',
  bodyTextAlignment: 'left',
  border: `1px solid ${Colors.adamantium}`,
  borderRadius: 'default',
  headingTag: 'h4',
  imageAlignment: 'flex-start',
  imageAlt: 'Help box missing image',
  imageRight: false,
  imageSrc: '',
  padding: 20,
};

export default HelpBox;
